import React, { useState, useEffect, useRef, useCallback } from "react";

import dayjs from "dayjs";
import { BSON as Oid } from "realm-web";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Tooltip from "@mui/material/Tooltip";
import Modal from "@mui/material/Modal";
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
} from "@mui/material";

import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";
import { showMessage } from "@fuse/core/FuseMessage/fuseMessageSlice";

import { useTranslation } from "react-i18next";

import { useAppDispatch } from "app/store/hooks";
import { useDataStore } from "app/store/dataStore";
import { useSocketStore } from "app/store/socketStore";
import {
  getUrlParams,
  beforeUploading,
  getSalesContext,
  SalesContext,
} from "app/store/dataUtils";

import { resolveChatContext } from "src/app/utils";
import { IImages, IMessage } from "src/app/types";
import { ChatMessage } from "src/app/models/chat-message";
import { ChatImageModel } from "src/app/models/chat-image";

import ChatImageUpload from "./components/ChatImageUpload";
import ChatsEmojiPicker from "./components/ChatsEmojiPicker";
import ChatMessageSuspense from "./ChatMessageSuspense";

const FormatDateSeparator = ({ date }: { date: Date }) => {
  const now = dayjs();
  const messageDate = dayjs(date);

  if (messageDate.isSame(now, "day")) {
    return "Today";
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    return "Yesterday";
  } else if (messageDate.isSame(now, "year")) {
    return messageDate.format("dddd, MMMM YYYY");
  } else {
    return messageDate.format("MMMM YYYY");
  }
};

function DateSeparator({ date }: { date: Date }) {
  return (
    <div className="flex flex-row items-center justify-center py-10">
      <Tooltip title={dayjs(date).format("MMMM D, YYYY, h:mm A")}>
        <Typography
          variant="caption"
          color="textSecondary"
          className="rounded-[var(--rc-radius-bubble,12px)] bg-[color:var(--rc-bubble-in-bg,#f3f4f6)] hover:bg-[color:var(--rc-selected,#e5e7eb)] px-10 py-2 cursor-pointer"
        >
          <FormatDateSeparator date={date} />
        </Typography>
      </Tooltip>
    </div>
  );
}

const MemoizedChatMessageSuspense = React.memo(ChatMessageSuspense);

interface ChatMessageProps {
  messageList: Partial<IMessage>[];
  handleNewMessage: (message: Partial<IMessage>) => void;
  handleMessages: (messages: Partial<IMessage>[]) => void;
  salesContext?: SalesContext | null;
  salesChatId?: string;
  salesShipment?: string;
}

function ChatMessageInterface({
  messageList = [],
  handleNewMessage,
  handleMessages,
  salesContext = null,
  salesChatId = "",
  salesShipment = "",
}: ChatMessageProps) {
  const dispatch = useAppDispatch();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("lg"));
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [text, setText] = useState("");
  const [preview, setPreview] = useState(false);

  const { t: trl } = useTranslation();
  const {
    imageDoc,
    chatMessageDoc,
    currentChatRoom,
    updateChatMessageDoc,
    selectedImages,
    popSelectedImage,
    resetSelectedImages,
    setCurrentChatRoom,
  } = useDataStore();
  const { driver, warehouse, shipment, sidebarEnabled, chatId } =
    resolveChatContext(getUrlParams());
  const { version, setVersion } = useDataStore();
  const { messageSocket } = useSocketStore();

  const resolvedDriver = sidebarEnabled ? currentChatRoom?.driver : driver;
  const resolvedWarehouse = sidebarEnabled
    ? currentChatRoom?.warehouse
    : warehouse;
  const resolvedShipment = sidebarEnabled
    ? currentChatRoom?.shipment
    : shipment;

  // Determine chat context and whether composer is enabled
  const hasChatContext = salesContext
    ? !!salesChatId
    : sidebarEnabled
      ? !!currentChatRoom
      : !!resolvedDriver && !!resolvedWarehouse;
  // Drivers tab (WAREHOUSE type) is interactive; Shipments tab is read-only.
  // Sales mode is interactive for both sides once the thread is resolved.
  const enableChatInput = salesContext
    ? !!salesChatId
    : sidebarEnabled
      ? currentChatRoom?.type === "WAREHOUSE" &&
        !!resolvedDriver &&
        !!resolvedWarehouse
      : !!resolvedDriver && !!resolvedWarehouse;
  const ensureChatId = async (): Promise<string> => {
    if (chatId) {
      return chatId;
    }

    if (!resolvedDriver || !resolvedWarehouse) {
      dispatch(showMessage({ message: "Unable to start chat" }));
      return "";
    }

    try {
      const [{ default: axios }, { appConfig }] = await Promise.all([
        import("axios"),
        import("app/configs/appConfig"),
      ]);

      const response = await axios.get(
        `${appConfig.serverChatBaseURL}/chat_id`,
        {
          params: {
            driver: resolvedDriver,
            warehouse: resolvedWarehouse,
            shipment: resolvedShipment,
          },
        },
      );

      const newChatId = response?.data;

      if (newChatId) {
        if (sidebarEnabled && currentChatRoom) {
          setCurrentChatRoom({ ...currentChatRoom, _id: newChatId });
        }
        if (messageSocket) {
          messageSocket.emit("chat-room::join", { chatId: newChatId });
          if (sidebarEnabled && resolvedWarehouse) {
            messageSocket.emit("driver-chat-rooms::fetch", {
              warehouseId: resolvedWarehouse,
            });
          }
        }
        return newChatId;
      }
    } catch (error) {
      console.error("Failed to ensure chat id:", error);
      dispatch(showMessage({ message: "Unable to start chat" }));
    }

    return "";
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const openUploadPopup = () => {
    if (!enableChatInput) return;
    const _chatMsg = new ChatMessage({
      text,
      chat_id: salesContext ? salesChatId : chatId,
      contains_images: true,
      driver: salesContext ? "" : resolvedDriver,
      warehouse: salesContext ? "" : resolvedWarehouse,
      shipment: salesContext ? salesShipment : resolvedShipment || "",
      ...(salesContext ? { source: salesContext.role } : {}),
    });
    updateChatMessageDoc(_chatMsg.docValues);
    setPreview(true);
  };

  const handleSendMessage = async () => {
    if (!text.trim() || !enableChatInput) return;

    if (salesContext) {
      if (!salesChatId) return;
      setText("");
      resetSelectedImages();

      const _chatMsg = new ChatMessage({
        text,
        chat_id: salesChatId,
        contains_images: false,
        driver: "",
        warehouse: "",
        shipment: salesShipment,
        source: salesContext.role,
      });
      handleNewMessage({ ..._chatMsg.docValues, sending: true });
      messageSocket?.emit("sales-chat-message::create", {
        message: _chatMsg.docValues,
      });
      return;
    }

    const activeChatId = chatId || (await ensureChatId());
    if (!activeChatId) return;

    setText("");
    resetSelectedImages();

    const _chatMsg = new ChatMessage({
      text,
      chat_id: activeChatId,
      contains_images: false,
      driver: resolvedDriver,
      warehouse: resolvedWarehouse,
      shipment: resolvedShipment || "",
    });
    handleNewMessage({ ..._chatMsg.docValues, sending: true });
    await _chatMsg.sendMessage();
  };

  const handleSendImage = async (message: Partial<IMessage>) => {
    const activeChatId = salesContext
      ? salesChatId
      : chatId || (await ensureChatId());
    if (!activeChatId) return;

    setText("");
    resetSelectedImages();

    const messageWithContext: Partial<IMessage> = {
      ...(message || {}),
      chat_id: activeChatId,
      driver: salesContext ? "" : resolvedDriver,
      warehouse: salesContext ? "" : resolvedWarehouse,
      shipment: salesContext ? salesShipment : resolvedShipment || "",
      ...(salesContext ? { source: salesContext.role } : {}),
    };

    const _this = new ChatImageModel(imageDoc);
    handleNewMessage({
      ...messageWithContext,
      chat_images: imageDoc,
      sending: true,
    });
    await _this.sendImages(messageWithContext, imageDoc);
  };

  const handleImageUpload = async () => {
    await handleSendImage(chatMessageDoc || {});
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files?.length) beforeUploading(files);
  };

  const triggerSendEvent = async () => {
    if (!enableChatInput) return;
    if (selectedImages.length > 0) {
      await handleImageUpload();
    } else {
      await handleSendMessage();
    }
  };

  function getChatHeaderAvatar() {
    const title = salesContext
      ? salesShipment
      : sidebarEnabled
        ? currentChatRoom?.driver
        : driver;
    if (!title) return "";
    const parts = title.trim().split(/\s+/);
    return parts.length === 1
      ? parts[0].slice(0, 2).toUpperCase()
      : parts.map((part) => part[0].toUpperCase()).join("");
  }

  useEffect(() => {
    scrollToBottom();
  }, [messageList]);

  return (
    <div className="w-full">
      {hasChatContext ? (
        <Card className="shadow-none rounded-none">
          <Modal
            className={`grid place-items-center ${preview ? "visible" : "invisible"}`}
            sx={{
              backdropFilter: "blur(1px)",
              backgroundColor: "rgba(0, 0, 0, 0.01)",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              opacity: preview ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
              pointerEvents: preview ? "auto" : "none",
            }}
            open={preview}
            onClose={() => setPreview(false)}
          >
            <ChatImageUpload togglePreview={setPreview} />
          </Modal>

          <CardHeader
            className="h-full font-[Inter] border-b shadow-xs"
            title={
              <div className="flex h-44 items-center gap-6">
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "var(--rc-chrome, #02163B)",
                    fontSize: "14px",
                    fontWeight: 600,
                    borderRadius: "var(--rc-radius, 8%)",
                  }}
                >
                  {getChatHeaderAvatar()}
                </Avatar>
                <div className="flex flex-col gap-0.5">
                  <Typography className="text-[15px] font-semibold text-[color:var(--rc-ink,#111827)] m-0 leading-5">
                    {salesContext
                      ? salesShipment
                      : sidebarEnabled
                        ? currentChatRoom?.driver_name || "Driver"
                        : driver || "Driver"}
                  </Typography>
                  <Typography className="text-[11.5px] text-[color:var(--rc-ink-soft,#4b5563)] font-medium m-0 leading-4">
                    {salesContext
                      ? trl("comms_salesConversation")
                      : sidebarEnabled && currentChatRoom?.type === "WAREHOUSE"
                        ? currentChatRoom?.warehouse || "Warehouse"
                        : sidebarEnabled && currentChatRoom?.shipment
                          ? currentChatRoom?.shipment || "Shipment"
                          : warehouse}
                  </Typography>
                </div>
              </div>
            }
          />

          <CardContent className="h-[calc(100vh-180px)] flex flex-col p-0 overflow-auto gap-12 bg-[color:var(--rc-surface,#F8F9FA)]">
            <div className="pb-10 overflow-auto">
              <List className="overflow-y-auto">
                {messageList?.length > 0 &&
                  messageList.map((message, index) => {
                    const currentDate = dayjs(message?.created_at);
                    const prevDate =
                      index > 0
                        ? dayjs(messageList[index - 1]?.created_at)
                        : null;
                    const showDateSeparator =
                      !prevDate || !currentDate.isSame(prevDate, "day");

                    return (
                      <React.Fragment key={message._id ?? index}>
                        {showDateSeparator && (
                          <DateSeparator date={currentDate.toDate()} />
                        )}
                        <MemoizedChatMessageSuspense
                          message={message}
                          idx={index}
                        />
                      </React.Fragment>
                    );
                  })}
                <div ref={messagesEndRef} />
              </List>
            </div>
          </CardContent>

          {enableChatInput && (
            <CardActions
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="flex flex-col justify-end gap-1 relative bg-[color:var(--rc-surface,#F8F9FA)]"
            >
              {selectedImages?.length > 0 && (
                <section
                  id="preview_images_zone"
                  className="w-auto flex flex-row gap-8 items-center bg-gray-50 dark:bg-gray-800 pl-16 pr-16 py-8 border-t border-gray-200"
                >
                  {selectedImages.map((image, idx) => (
                    <img
                      key={idx}
                      src={image}
                      title="Click to remove"
                      onClick={() => popSelectedImage(idx)}
                      className="w-[80px] h-[80px] object-contain bg-gray-50 rounded-[8px] cursor-pointer border-2 border-gray-200 hover:border-red-300"
                      alt={`preview-${idx}`}
                    />
                  ))}
                </section>
              )}

              <div className="w-full p-5">
                <div
                  className="flex items-center bg-white border border-[color:var(--rc-accent-soft,#ACC8FC)] rounded-[var(--rc-radius,8px)] w-full h-64 px-2"
                  style={{
                    boxShadow:
                      "inset 0 0 1px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <Grid
                    item
                    xs={1}
                    className="relative w-[65px] h-[55px] rounded-[var(--rc-radius,12px)] flex flex-row gap-36 lg:gap-x-48 items-center justify-center hover:bg-[color:var(--rc-hover,#f3f4f6)] shadow-1 shadow-grey-50 cursor-pointer"
                  >
                    <FuseSvgIcon
                      aria-disabled={!enableChatInput}
                      className="dark:text-gray-100 text-gray-600"
                      size={!isMobile ? 20 : 16}
                      color="action"
                      onClick={openUploadPopup}
                    >
                      feather:paperclip
                    </FuseSvgIcon>
                  </Grid>

                  <input
                    type="text"
                    disabled={!enableChatInput}
                    placeholder={trl("comms_input_placeholder")}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        triggerSendEvent();
                      }
                    }}
                    className="flex-1 border-0 outline-blue-200 bg-transparent text-[15px] font-medium text-gray-600 placeholder-gray-500 py-1"
                  />

                  <Grid
                    onClick={triggerSendEvent}
                    item
                    xs={1}
                    className={`w-[65px] h-[55px] rounded-[var(--rc-radius,12px)] hover:bg-[color:var(--rc-accent-faint,#dbeafe)] shadow-1 shadow-grey-50 grid place-items-center ml-auto ${
                      !enableChatInput ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  >
                    <FuseSvgIcon
                      className="text-50 rounded-full text-gray-600"
                      size={24}
                      style={{ color: "var(--rc-accent, #0741AC)" }}
                      aria-disabled={!enableChatInput || !text.trim()}
                    >
                      material-outline:send
                    </FuseSvgIcon>
                  </Grid>
                </div>
              </div>
            </CardActions>
          )}
        </Card>
      ) : (
        <div className="h-full flex flex-col items-center justify-center gap-10">
          <svg
            width="56"
            height="56"
            viewBox="0 0 91 91"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="1"
              y="1.19678"
              width="89"
              height="89"
              rx="42.0459"
              fill="#EFF0F4"
            />
            <rect
              x="1"
              y="1.19678"
              width="89"
              height="89"
              rx="42.0459"
              stroke="#D8D8D8"
            />
            <path
              d="M31.25 52.8218C29.9383 52.8218 28.875 51.7585 28.875 50.4468C28.875 49.1351 29.9383 48.0718 31.25 48.0718C32.5617 48.0718 33.625 49.1351 33.625 50.4468C33.625 51.7585 32.5617 52.8218 31.25 52.8218Z"
              fill="#79777F"
            />
            <path
              d="M38.375 50.4468C38.375 51.7585 39.4383 52.8218 40.75 52.8218C42.0617 52.8218 43.125 51.7585 43.125 50.4468C43.125 49.1351 42.0617 48.0718 40.75 48.0718C39.4383 48.0718 38.375 49.1351 38.375 50.4468Z"
              fill="#79777F"
            />
            <path
              d="M50.25 52.8218C48.9383 52.8218 47.875 51.7585 47.875 50.4468C47.875 49.1351 48.9383 48.0718 50.25 48.0718C51.5617 48.0718 52.625 49.1351 52.625 50.4468C52.625 51.7585 51.5617 52.8218 50.25 52.8218Z"
              fill="#79777F"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M65.6353 49.1358L59.7415 49.8726C59.7471 50.0633 59.75 50.2547 59.75 50.4468C59.75 60.9402 51.2434 69.4468 40.75 69.4468C39.1803 69.4468 37.6519 69.2559 36.1883 68.895L25.9784 67.6188C23.4673 67.3049 21.7222 64.9642 22.1382 62.468L22.7645 58.7103L22.3017 55.0085C21.9409 53.5448 21.75 52.0165 21.75 50.4468C21.75 39.9534 30.2566 31.4468 40.75 31.4468C41.0199 31.4468 41.2885 31.4524 41.5557 31.4636C43.5072 25.9212 48.7869 21.9468 55 21.9468C62.8701 21.9468 69.25 28.3267 69.25 36.1968C69.25 37.3592 69.1103 38.4921 68.8458 39.5784L68.5324 42.0853L68.9635 44.6718C69.3241 46.8352 67.8116 48.8638 65.6353 49.1358ZM55 26.6968C60.2467 26.6968 64.5 30.9501 64.5 36.1968C64.5 37.0139 64.3974 37.8036 64.2056 38.5552L64.1686 38.6998L63.803 41.625C63.7567 41.9952 63.7644 42.3701 63.8257 42.7382L64.1256 44.5376L59.0097 45.1771C57.2382 39.0278 52.4445 34.159 46.3405 32.2826C47.8322 28.9876 51.1508 26.6968 55 26.6968ZM40.75 36.1968C32.8799 36.1968 26.5 42.5767 26.5 50.4468C26.5 51.6706 26.6537 52.8551 26.9417 53.9835L26.9786 54.1281L27.4831 58.1641C27.5365 58.5913 27.5277 59.0239 27.4569 59.4485L26.8744 62.9438L37.0687 64.2181L37.2133 64.255C38.3417 64.543 39.5262 64.6968 40.75 64.6968C48.6201 64.6968 55 58.3168 55 50.4468C55 42.5767 48.6201 36.1968 40.75 36.1968Z"
              fill="#79777F"
            />
            <path
              d="M31.25 52.8218C29.9383 52.8218 28.875 51.7585 28.875 50.4468C28.875 49.1351 29.9383 48.0718 31.25 48.0718C32.5617 48.0718 33.625 49.1351 33.625 50.4468C33.625 51.7585 32.5617 52.8218 31.25 52.8218Z"
              stroke="#EFF0F4"
            />
            <path
              d="M38.375 50.4468C38.375 51.7585 39.4383 52.8218 40.75 52.8218C42.0617 52.8218 43.125 51.7585 43.125 50.4468C43.125 49.1351 42.0617 48.0718 40.75 48.0718C39.4383 48.0718 38.375 49.1351 38.375 50.4468Z"
              stroke="#EFF0F4"
            />
            <path
              d="M50.25 52.8218C48.9383 52.8218 47.875 51.7585 47.875 50.4468C47.875 49.1351 48.9383 48.0718 50.25 48.0718C51.5617 48.0718 52.625 49.1351 52.625 50.4468C52.625 51.7585 51.5617 52.8218 50.25 52.8218Z"
              stroke="#EFF0F4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M65.6353 49.1358L59.7415 49.8726C59.7471 50.0633 59.75 50.2547 59.75 50.4468C59.75 60.9402 51.2434 69.4468 40.75 69.4468C39.1803 69.4468 37.6519 69.2559 36.1883 68.895L25.9784 67.6188C23.4673 67.3049 21.7222 64.9642 22.1382 62.468L22.7645 58.7103L22.3017 55.0085C21.9409 53.5448 21.75 52.0165 21.75 50.4468C21.75 39.9534 30.2566 31.4468 40.75 31.4468C41.0199 31.4468 41.2885 31.4524 41.5557 31.4636C43.5072 25.9212 48.7869 21.9468 55 21.9468C62.8701 21.9468 69.25 28.3267 69.25 36.1968C69.25 37.3592 69.1103 38.4921 68.8458 39.5784L68.5324 42.0853L68.9635 44.6718C69.3241 46.8352 67.8116 48.8638 65.6353 49.1358ZM55 26.6968C60.2467 26.6968 64.5 30.9501 64.5 36.1968C64.5 37.0139 64.3974 37.8036 64.2056 38.5552L64.1686 38.6998L63.803 41.625C63.7567 41.9952 63.7644 42.3701 63.8257 42.7382L64.1256 44.5376L59.0097 45.1771C57.2382 39.0278 52.4445 34.159 46.3405 32.2826C47.8322 28.9876 51.1508 26.6968 55 26.6968ZM40.75 36.1968C32.8799 36.1968 26.5 42.5767 26.5 50.4468C26.5 51.6706 26.6537 52.8551 26.9417 53.9835L26.9786 54.1281L27.4831 58.1641C27.5365 58.5913 27.5277 59.0239 27.4569 59.4485L26.8744 62.9438L37.0687 64.2181L37.2133 64.255C38.3417 64.543 39.5262 64.6968 40.75 64.6968C48.6201 64.6968 55 58.3168 55 50.4468C55 42.5767 48.6201 36.1968 40.75 36.1968Z"
              stroke="#EFF0F4"
            />
          </svg>

          <Typography className="text-[#605E5C] text-md">
            Choose a conversation to view messages and details
          </Typography>
        </div>
      )}
    </div>
  );
}

export function ChatInterface() {
  const dispatch = useAppDispatch();

  const [messageList, setMessageList] = useState<Partial<IMessage>[]>([]);
  const [salesChatId, setSalesChatId] = useState<string>("");
  const salesContext = getSalesContext();
  const [salesShipment, setSalesShipment] = useState<string>(
    salesContext?.shipments.length === 1 ? salesContext.shipments[0] : "",
  );
  const { messageSocket, isConnected } = useSocketStore();
  const { t: trl } = useTranslation();
  const { currentChatRoom, currentWarehouse, setCurrentChatRoom, setVersion } =
    useDataStore();
  const { driver, warehouse, shipment, sidebarEnabled } =
    resolveChatContext(getUrlParams());
  const lastSeenTimestampRef = useRef<number>(0);
  const markingSeenRef = useRef<boolean>(false);

  // Stable callback references to prevent listener churn
  const handleMessages = useCallback((messages: Partial<IMessage>[]) => {
    setMessageList(messages);
  }, []);

  // Normalize _id to a hex string regardless of format (ObjectId instance, hex string, or {$oid} EJSON)
  const normalizeMessageId = useCallback((id: any): string => {
    if (!id) return "";
    if (typeof id === "string") return id;
    if (typeof id === "object") {
      if (id.$oid) return id.$oid;
      if (typeof id.toHexString === "function") return id.toHexString();
    }
    try {
      return new Oid.ObjectId(id).toString();
    } catch {
      return String(id);
    }
  }, []);

  const handleNewMessage = useCallback(
    (message: Partial<IMessage>) => {
      setMessageList((prevMessages) => {
        const msgId = normalizeMessageId(message._id);
        if (Array.isArray(prevMessages)) {
          const messageExists = prevMessages.some(
            (prevMsg) => normalizeMessageId(prevMsg._id) === msgId,
          );
          if (!messageExists) {
            return [...prevMessages, message];
          } else {
            const updatedMessages = prevMessages.map((prevMsg) => {
              if (normalizeMessageId(prevMsg._id) === msgId) {
                return { ...prevMsg, ...message };
              }
              return prevMsg;
            });
            return updatedMessages;
          }
        } else return [message];
      });
    },
    [normalizeMessageId],
  );

  // Debounced function to mark messages as seen - only once per chat room session
  const markMessagesAsSeen =
    useRef<(chatId: string, shipmentId?: string) => void>();

  useEffect(() => {
    markMessagesAsSeen.current = (chatId: string, shipmentId?: string) => {
      if (!messageSocket || markingSeenRef.current) return;

      const now = Date.now();
      // Prevent multiple calls within 1 second
      if (now - lastSeenTimestampRef.current < 1000) return;

      markingSeenRef.current = true;
      lastSeenTimestampRef.current = now;

      messageSocket.emit("message::seen", {
        chat_id: chatId,
        shipment: shipmentId || "",
        date: now,
      });

      // Reset the flag after a short delay
      setTimeout(() => {
        markingSeenRef.current = false;
      }, 500);
    };
  }, [messageSocket]);

  const handleFetchMessages = async () => {
    if (salesContext) {
      // Sales mode: with a single scoped shipment, join its thread directly;
      // with a list, the sales sidebar drives the join. Either way the server
      // answers with `sales-chat`, which triggers the message fetch below.
      if (salesContext.shipments.length === 1) {
        messageSocket?.emit("sales-chat::join", {
          shipment: salesContext.shipments[0],
        });
      }
      return;
    }
    if (!sidebarEnabled) {
      dispatch(showMessage({ message: trl("comms_loading") }));
      const chatModel = new ChatMessage(
        { driver, shipment, warehouse },
        messageSocket,
      );
      try {
        const msgResult = await chatModel.fetchMessages();
        handleMessages(msgResult);
      } catch (error) {
        console.error("Failed to fetch messages:", error);
        handleMessages([]);
      }
    } else if (sidebarEnabled && currentChatRoom) {
      const { type, _id: chatId } = currentChatRoom;
      let event = "";
      if (type === "WAREHOUSE") {
        event = "driver-chat-messages::fetch";
      } else if (type === "SHIPMENT") {
        event = "shipment-chat-messages::fetch";
      }
      if (event && chatId) {
        messageSocket.emit(event, { chatId });
        // Ensure we are joined to receive room broadcasts immediately
        messageSocket.emit("chat-room::join", { chatId });
      } else if (event && !chatId) {
        // No chat exists yet - show empty state with temporary snackbar
        setTimeout(
          () =>
            dispatch(
              showMessage({
                message: trl("comms_noChats"),
                autoHideDuration: 3000,
              }),
            ),
          500,
        );
        handleMessages([]);
      }
    }
  };

  // Stable callback references using useCallback to prevent listener churn
  const handleMessagesEvent = useCallback(
    (data: Partial<IMessage>[]) => {
      if (!data?.length) {
        setTimeout(
          () => dispatch(showMessage({ message: trl("comms_noChats") })),
          1000,
        );
        handleMessages([]);
        return;
      }
      handleMessages(data);
    },
    [dispatch, trl, handleMessages],
  );

  const onDriverChatMessages = useCallback(
    (data: Partial<IMessage>[]) => {
      if (!data?.length) {
        setTimeout(
          () => dispatch(showMessage({ message: trl("comms_noChats") })),
          1000,
        );
        handleMessages([]);
        return;
      }
      handleMessages(data);
      // mark driver messages as seen when viewing driver chat rooms (WAREHOUSE type)
      try {
        const room = useDataStore.getState().currentChatRoom;
        if (
          room?.type === "WAREHOUSE" &&
          room?._id &&
          markMessagesAsSeen.current
        ) {
          markMessagesAsSeen.current(room._id, room?.shipment);
        }
      } catch {}
    },
    [dispatch, trl, handleMessages],
  );

  // Handle new messages and mark as seen
  const onMessagesUpdated = useCallback(
    (message: Partial<IMessage>) => {
      console.log("[socket] messages-updated", message);
      handleNewMessage(message);
      try {
        const room = useDataStore.getState().currentChatRoom;
        // Normalize both sides before comparing — message.chat_id may be an ObjectId
        const msgChatId = normalizeMessageId(message?.chat_id);
        const roomChatId = normalizeMessageId(room?._id);
        // If we're currently viewing this chat room, mark the new message as seen
        if (
          room?.type === "WAREHOUSE" &&
          roomChatId &&
          msgChatId === roomChatId &&
          markMessagesAsSeen.current
        ) {
          markMessagesAsSeen.current(roomChatId, room?.shipment);
        }
      } catch {}
    },
    [handleNewMessage, normalizeMessageId],
  );

  const handleVersionEvent = useCallback(
    (value: any) => setVersion(value),
    [setVersion],
  );

  const handleMessagesSocketEvent = useCallback(
    (messages: Partial<IMessage>[]) => {
      if (!messages?.length) {
        dispatch(showMessage({ message: trl("comms_noChats") }));
      }
      handleMessages(messages);
    },
    [dispatch, trl, handleMessages],
  );

  const onSalesChatResolved = useCallback(
    (data: { chatId?: string; shipment?: string }) => {
      if (!data?.chatId) return;
      setSalesChatId(data.chatId);
      if (data.shipment) setSalesShipment(data.shipment);
      messageSocket?.emit("sales-chat-messages::fetch", {
        chatId: data.chatId,
      });
    },
    [messageSocket],
  );

  const onSalesChatMessages = useCallback(
    (data: Partial<IMessage>[]) => {
      handleMessages(data || []);
    },
    [handleMessages],
  );

  // Single useEffect for all socket event listeners with stable callback references
  useEffect(() => {
    if (!messageSocket || !messageSocket.connected) return;

    // Register all event listeners
    messageSocket.on("messages", handleMessagesSocketEvent);
    messageSocket.on("app-version", handleVersionEvent);
    messageSocket.on("driver-chat-messages", onDriverChatMessages);
    messageSocket.on("shipment-chat-messages", handleMessagesEvent);
    messageSocket.on("sales-chat", onSalesChatResolved);
    messageSocket.on("sales-chat-messages", onSalesChatMessages);
    messageSocket.on("messages-updated", onMessagesUpdated);

    return () => {
      // Clean up all listeners
      messageSocket.off("messages", handleMessagesSocketEvent);
      messageSocket.off("app-version", handleVersionEvent);
      messageSocket.off("driver-chat-messages", onDriverChatMessages);
      messageSocket.off("shipment-chat-messages", handleMessagesEvent);
      messageSocket.off("sales-chat", onSalesChatResolved);
      messageSocket.off("sales-chat-messages", onSalesChatMessages);
      messageSocket.off("messages-updated", onMessagesUpdated);
    };
  }, [
    messageSocket,
    handleMessagesSocketEvent,
    handleVersionEvent,
    onDriverChatMessages,
    handleMessagesEvent,
    onSalesChatResolved,
    onSalesChatMessages,
    onMessagesUpdated,
  ]);

  useEffect(() => {
    if (isConnected) {
      setMessageList(null);
      handleFetchMessages();
    }
  }, [isConnected, currentChatRoom, sidebarEnabled]);

  // Reset the seen timestamp when changing chat rooms
  useEffect(() => {
    lastSeenTimestampRef.current = 0;
    markingSeenRef.current = false;
  }, [currentChatRoom?._id]);

  return (
    <ChatMessageInterface
      handleNewMessage={handleNewMessage}
      handleMessages={handleMessages}
      messageList={messageList}
      salesContext={salesContext}
      salesChatId={salesChatId}
      salesShipment={salesShipment}
    />
  );
}
